import { useState, useCallback, useEffect, useRef } from "react";
import { useLocalStorage, useSessionStorage } from "usehooks-ts";
import { useSearchParams } from "react-router-dom";
import { PMGradingRow } from "../workers/pmui";

/**
 * Custom hook for managing PM Grading tabs and associated data
 */
export const usePMGradingTabs = () => {
  // Tab management
  const [tabs, setTabs] = useLocalStorage<string[]>("PMRefurbUITabs", []);
  const [activeTab, setActiveTab] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab data management
  const [pmRows, setPMRows] = useSessionStorage<Record<string, PMGradingRow>>(
    "PMRefurbUIRowData",
    {}
  );
  const [lastRetrieved, setLastRetrieved] = useSessionStorage<
    Record<string, number>
  >("PMRefurbUIRowLastRetrieved", {});
  const [lastModified, setLastModified] = useSessionStorage<
    Record<string, number>
  >("PMRefurbUIRowLastModified", {});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Handle adding a new tab
  const addTab = useCallback(
    (entityId: string) => {
      if (!tabs.includes(entityId)) {
        setTabs([...tabs, entityId]);
      }
      setActiveTab(entityId);
      setSearchParams({ pmid: entityId });
    },
    [tabs, setTabs, setSearchParams] // activeTab is not needed since it's only set, not read
  );

  // Handle removing a tab
  const removeTab = useCallback(
    (entityId: string) => {
      const newTabs = tabs.filter((id) => id !== entityId);
      setTabs(newTabs);

      // Update active tab if the removed tab was active
      if (activeTab === entityId) {
        const newActiveTab =
          newTabs.length > 0 ? newTabs[newTabs.length - 1] : "";
        setActiveTab(newActiveTab);

        if (newActiveTab) {
          setSearchParams({ pmid: newActiveTab });
        } else {
          // Always clear URL parameters when closing a tab with no remaining tabs
          setSearchParams({});
        }
      } else if (searchParams.get("pmid") === entityId) {
        // If the URL still has this entity ID even though it's not active, clear it
        setSearchParams({});
      }
      // Clean up related data when tab is closed
      setPMRows((prev) => {
        const newData = { ...prev };
        delete newData[entityId];
        return newData;
      });

      setLastRetrieved((prev) => {
        const newData = { ...prev };
        delete newData[entityId];
        return newData;
      });

      setLastModified((prev) => {
        const newData = { ...prev };
        delete newData[entityId];
        return newData;
      });
    },
    [
      activeTab,
      tabs,
      searchParams,
      setTabs,
      setSearchParams,
      setPMRows,
      setLastRetrieved,
      setLastModified,
    ]
  );

  // Handle switching tabs
  const switchTab = useCallback(
    (entityId: string) => {
      if (tabs.includes(entityId)) {
        setActiveTab(entityId);
        // Update URL parameters when switching tabs
        setSearchParams({ pmid: entityId });
      } else {
        // If the tab doesn't exist anymore, clear the URL parameter
        setSearchParams({});
      }
    },
    [tabs, setSearchParams]
  );

  // One-time initialization from URL on component mount
  const syncActiveTabWithUrl = useCallback(() => {
    const pmid = searchParams.get("pmid");

    // Only process URL parameters if there is a pmid and we have tabs
    // This prevents re-adding tabs that were just removed
    if (pmid && tabs.length > 0) {
      // Only add new tabs during initial load, not after tab removal
      if (tabs.includes(pmid)) {
        // If already present, just activate it
        setActiveTab(pmid);
      }
      // We're removing the auto-adding of tabs based on URL after initial mount
    } else if (tabs.length > 0 && !activeTab) {
      // Set the first tab as active if no tab is active
      setActiveTab(tabs[0]);
      setSearchParams({ pmid: tabs[0] });
    }
  }, [searchParams, tabs, setSearchParams, activeTab]);

  // Set loading state for a tab
  const setTabLoading = useCallback((entityId: string, isLoading: boolean) => {
    setLoading((prev) => ({ ...prev, [entityId]: isLoading }));
  }, []);

  // Update pmRow data for a specific tab
  const updatePMRow = useCallback(
    (entityId: string, rowData: PMGradingRow) => {
      setPMRows((prev) => ({ ...prev, [entityId]: rowData }));
      setLastRetrieved((prev) => ({ ...prev, [entityId]: Date.now() }));
    },
    [setPMRows, setLastRetrieved]
  );

  // Mark a tab as modified
  const markTabModified = useCallback(
    (entityId: string) => {
      setLastModified((prev) => ({ ...prev, [entityId]: Date.now() }));
    },
    [setLastModified]
  );

  // Initialize the component - only during mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      // Only sync with URL during initial mount
      syncActiveTabWithUrl();
      isInitialMount.current = false;
    }
  }, [syncActiveTabWithUrl]);

  // Update document title based on active tab
  useEffect(() => {
    if (activeTab) {
      window.document.title = `${activeTab} | PM Grading | ClearCycle Circle`;
    } else {
      window.document.title = `PM Grading | ClearCycle Circle`;
    }

    // Return the title to normal after unmount
    return () => {
      window.document.title = `ClearCycle Circle`;
    };
  }, [activeTab]);

  return {
    tabs,
    activeTab,
    pmRows,
    lastRetrieved,
    lastModified,
    loading,
    addTab,
    removeTab,
    switchTab,
    setTabLoading,
    updatePMRow,
    markTabModified,
  };
};
